"use client";
import React, { useState, useEffect } from "react";
import Head from "next/head";
import { useParams } from "next/navigation";
import Link from "next/link";
import parse, { domToReact } from "html-react-parser";
import DOMPurify from "dompurify";
import { decode } from "html-entities";
import Image from "next/image";

const sanitizeContent = (content) => {
  const trimmedContent = content.trim();
  const hasSingleRoot = /^<[^>]+>.*<\/[^>]+>$/.test(trimmedContent);
  const wrappedContent = hasSingleRoot
    ? trimmedContent
    : `<div>${trimmedContent}</div>`;

  return DOMPurify.sanitize(wrappedContent, {
    ALLOWED_TAGS: [
      "p",
      "a",
      "div",
      "span",
      "h1",
      "h2",
      "h3",
      "h4",
      "ul",
      "ol",
      "li",
      "strong",
      "em",
      "blockquote",
      "iframe",
      "img",
      "figure",
      "br",
    ],
    ALLOWED_ATTR: [
      "href",
      "src",
      "alt",
      "title",
      "class",
      "target",
      "rel",
      "width",
      "height",
      "style",
    ],
  });
};

const FullBlog = () => {
  const { BlogSlug } = useParams();
  const [blogData, setBlogData] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPostAndImage = async () => {
      setLoading(true);
      setError(null);
      try {
        const postsRes = await fetch(
          `https://wp.netpractice.app/wp-json/wp/v2/posts?slug=${BlogSlug}`
        );
        if (!postsRes.ok)
          throw new Error(`Error fetching post: ${postsRes.statusText}`);

        const post = (await postsRes.json())[0];
        setBlogData(post);

        const ogImageUrl = post?.yoast_head_json?.og_image?.[0]?.url;
        if (ogImageUrl) {
          const correctedUrl = ogImageUrl.replace(
            /^https:\/\/netpractice\.app/,
            "https://wp.netpractice.app"
          );
          setImageUrl(correctedUrl);
        } else {
          setImageUrl(null);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPostAndImage();
  }, [BlogSlug]);

  const formatResponseText = (htmlContent) => {
    if (!htmlContent) return null;

    let formattedText = htmlContent;

    // Add Tailwind classes or other replacements if needed
    formattedText = formattedText.replace(
      /<p>/g,
      '<p class="text-lg text-neutral-500 my-2 font-medium">'
    );
    formattedText = formattedText.replace(
      /<h3 class="wp-block-heading">/g,
      '<p class="text-2xl font-bold my-8">'
    );
    formattedText = formattedText.replace(
      /<strong>/g,
      '<span class="text-xl">'
    );
    formattedText = formattedText.replace(/<em>/g, '<em class="italic">');
    formattedText = formattedText.replace(
      /<ul class="wp-block-list">/g,
      '<ul class="list-disc ml-6 my-2">'
    );
    formattedText = formattedText.replace(
      /<li>/g,
      '<li class="my-1 text-lg text-neutral-500 font-medium">'
    );
    formattedText = formattedText.replace(
      /<blockquote class="wp-block-quote is-layout-flow wp-block-quote-is-layout-flow">/g,
      '<blockquote class="border-l-4 border-neutral-300 pl-4 text-xl my-3">'
    );
    formattedText = formattedText.replace(
      /<h2>/g,
      '<h2 class="text-2xl font-semibold my-3">'
    );
    formattedText = formattedText.replace(
      /<figure class="wp-block-embed(.*?)">/g,
      '<figure class="wp-block-embed$1 my-4">'
    );
    formattedText = formattedText.replace(
      /<div class="wp-block-embed__wrapper">/g,
      '<div class="wp-block-embed__wrapper w-full ">'
    );
    formattedText = formattedText.replace(
      /<iframe>/g,
      '<iframe class="w-full ">'
    );

    formattedText = formattedText.replace(
      /<img([^>]*?)src="https:\/\/netpractice\.app([^"]*?)"([^>]*?)>/g,
      `<img$1src="https://wp.netpractice.app$2"$3 class="my-4 w-full h-auto" />`
    );

    // Clean line breaks
    formattedText = formattedText.replace(/\n/g, `<p class="my-2" />`);
    formattedText = formattedText.replace(
      /<p class="my-2" \/>(<p class="my-2" \/>)+/g,
      `<p class="my-2" />`
    );

    return formattedText;
  };

  const addColorToChildren = (nodes) =>
  React.Children.map(nodes, (child) =>
    React.isValidElement(child)
      ? React.cloneElement(child, { style: { color: 'inherit' }, children: addColorToChildren(child.props.children) })
      : child
  );

  const parsedContent = blogData?.content?.rendered
    ? parse(
        formatResponseText(sanitizeContent(decode(blogData.content.rendered))),
        {
          replace: (domNode) => {
            if (
              domNode.name === "a" &&
              domNode.attribs?.href?.startsWith("https://netpractice.app")
            ) {
              // Convert absolute URL to relative path for Next.js Link
              const href = domNode.attribs.href.replace(
                "https://netpractice.app",
                ""
              );
              return (
                <Link
                  href={href}
                  className="text-blue-500 hover:underline text-md-[!important]"
                  style={{ color: '#3b82f6' }} // optional inline color for safety
                >
                  {addColorToChildren(domToReact(domNode.children))}
                </Link>
              );
            }
          },
        }
      )
    : null;

  if (loading) return <div>Loading blog post...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!blogData)
    return (
      <div className="min-h-screen flex items-center justify-center text-center">
        <div>
          <h1 className="text-4xl font-bold mb-4">Blog Not Found</h1>
          <p className="text-lg text-gray-600">
            The blog you are looking for doesn’t exist.
          </p>
        </div>
      </div>
    );

  return (
    <div>
      <Head>
        <title>{decode(blogData.title?.rendered)}</title>
        <meta name="description" content={decode(
      blogData.excerpt?.rendered.replace(/<[^>]+>/g, "") || ""
    )} />
        <link rel="stylesheet" href="/globals.css" />
      </Head>
      <article>
        <header>
          <h1 className="text-4xl font-semibold mt-8 mb-1">
            {decode(blogData.title?.rendered)}
          </h1>
          <p className="text-md text-neutral-500 font-bold mb-8">
            {new Date(blogData.date)?.toLocaleDateString()}
          </p>
        </header>

        {imageUrl && (
          <div
            style={{
              maxWidth: "30%",
              overflow: "hidden",
              margin: "20px auto 50px",
            }}
          >
            <Image
              src={imageUrl}
              alt={blogData.title?.rendered}
              layout="responsive"
              width={400}
              height={300}
              className="w-full h-auto object-cover"
            />
          </div>
        )}
        {console.log(blogData?.content?.rendered)}

        <div>{parsedContent}</div>

        <footer></footer>
      </article>
    </div>
  );
};

export default FullBlog;
